import type { MetadataParser } from '@/scopes/search/search/infrastructure/api/metadataParser'
import type { SearchAPIFilter, SearchAPIFilterValue, SearchFilters } from './types'
import { createMetadataParser } from '@/scopes/search/search/infrastructure/api/metadataParser'

export interface FilterMapper {
  map: (filters: SearchAPIFilter[]) => SearchFilters
}

type Mappers = {
  [K in keyof SearchFilters]: (filter: SearchAPIFilter) => SearchFilters[K];
}

interface Dependencies {
  metaDataParser: MetadataParser
}

export function createFilterMapper(dependencies: Partial<Dependencies> = {}): FilterMapper {
  const { metaDataParser = createMetadataParser() } = dependencies

  const mappers: Mappers = {
    AccommodationType: multipleSelectMapper,
    ArrivalAirport: multipleSelectMapper,
    Campaigns: multipleSelectMapper,
    ChildrenFacilities: multipleSelectMapper,
    City: multipleSelectMapper,
    Collection: multipleSelectMapper,
    Country: multipleSelectMapper,
    CustomersRating: singleSelectMapper,
    DepartureDate: multipleSelectMapper,
    DepartureAirport: multipleSelectMapper,
    Discount: multipleSelectMapper,
    Duration: multipleSelectMapper,
    Facilities: multipleSelectMapper,
    InternetFacilities: multipleSelectMapper,
    Lastminute: singleSelectMapper,
    Mealplan: multipleSelectMapper,
    MetersToBeach: singleSelectMapper,
    MetersToCenter: singleSelectMapper,
    Region: multipleSelectMapper,
    RoomCount: singleSelectMapper,
    Stars: singleSelectMapper,
    Subregion: multipleSelectMapper,
    Themes: multipleSelectMapper,
    TransportType: singleSelectMapper,
    CampaignsAndDiscounts: multipleSelectMapper,
    HasAvailability: singleSelectMapper,
    HasAvailabilityNextSeason: singleSelectMapper,
    MetersToSlope: singleSelectMapper,
    MetersToSkiLift: singleSelectMapper,
    SkiAreaMinimumAltitudeMeters: singleSelectMapper,
    BedsInLivingroom: singleSelectMapper,
    BathroomCount: singleSelectMapper,
    SkiAreaSlopeKms: singleSelectMapper,
  }

  function map(filters: SearchAPIFilter[]): SearchFilters {
    return filters.reduce((acc, filter) => {
      const filterMapper = mappers[filter.type]

      if (!filterMapper)
        return acc

      const mappedFilter = filterMapper(filter)
      return { ...acc, [filter.type]: mappedFilter }
    }, {}) as SearchFilters
  }

  function multipleSelectMapper<M = object>(filter: SearchAPIFilter) {
    return {
      options: getOptionsFromFilter<M>(filter),
    }
  }

  function singleSelectMapper<M = object>(filter: SearchAPIFilter) {
    const options = getOptionsFromFilter<M>(filter)

    return {
      options,
    }
  }

  function getOptionsFromFilter<M>(filter: SearchAPIFilter) {
    return filter.values
      .filter(({ isAvailable }) => isAvailable)
      .map(({ caption, value, metadata, count, groupInfo }: SearchAPIFilterValue) => ({
        name: caption,
        value,
        metadata: metaDataParser.parse(metadata ?? '') as M,
        resultsCount: count,
        ...(groupInfo && { groupInfo }),
      }))
  }

  return { map }
}
